import { useUpdateThreadVisitedAt } from "@/hooks/useThreads.tsx";
import type { GetThreadResp } from "@/types/thread.ts";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";

/**
 * Atom to track currently active thread IDs.
 *
 * This tracks threads that are currently open in the UI — today that is the main
 * thread view (Thread.tsx).
 *
 * Used for:
 * - Marker.io data collection (bug reports include active thread data)
 * - User presence tracking (subscribers watch for changes)
 */
export const activeThreadIdsAtom = atom<Set<number>>(new Set<number>());

/**
 * Hook to track a thread ID in the activeThreadIds atom.
 * Automatically adds the thread ID on mount and removes it on unmount.
 *
 * This is a pure state management hook - it only updates the atom.
 * Other systems (like presence tracking) can subscribe to the atom for side effects.
 */
export const useTrackActiveThread = (threadId: number | undefined) => {
  const [, setActiveThreadIds] = useAtom(activeThreadIdsAtom);

  useEffect(() => {
    if (threadId && !Number.isNaN(threadId)) {
      // Add thread ID on mount
      setActiveThreadIds((prev: Set<number>) => {
        const newSet = new Set(prev);
        newSet.add(threadId);
        return newSet;
      });

      // Remove thread ID on unmount
      return () => {
        setActiveThreadIds((prev: Set<number>) => {
          const newSet = new Set(prev);
          newSet.delete(threadId);
          return newSet;
        });
      };
    }
  }, [threadId, setActiveThreadIds]);
};

/**
 * Track that the user is viewing a thread's detail view on a given surface
 * (full page, right sidebar, or the calendar's left panel).
 *
 * Bundles the two side effects every thread-detail surface must perform so a
 * new surface can't wire up one and silently forget the other:
 * - Presence: registers the thread as active (drives server-side notification
 *   suppression and Marker.io bug-report traces) via {@link useTrackActiveThread}.
 * - Read state: bumps the server's `lastVisitedAt` once the thread loads, which
 *   feeds the "caught up" markers.
 *
 * `threadId` is registered for presence immediately on mount; `thread` gates the
 * `lastVisitedAt` update until the thread has actually loaded.
 */
export const useTrackThreadDetailView = (
  threadId: number | undefined,
  thread: GetThreadResp | undefined,
) => {
  useTrackActiveThread(threadId);

  // Use the loaded thread's own id (undefined until it loads) rather than a
  // sentinel — the hook no-ops while undefined, so there's no path to a POST
  // against a placeholder id.
  const updateVisitedAt = useUpdateThreadVisitedAt({ threadId: thread?.id });
  useEffect(() => {
    if (thread) {
      updateVisitedAt();
    }
  }, [thread, updateVisitedAt]);
};
